import React, { useEffect, useState } from 'react';
import {
    FaChevronDown,
    FaChevronUp,
    FaLock,
    FaMapMarkerAlt,
    FaPlane,
    FaPlaneArrival,
    FaPlaneDeparture,
    FaPlus,
    FaTrash,
    FaUser,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import ErpTemplate from '../../components/ErpTemplate';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import { FinField, FinInput } from '../../components/FinField';
import api from '../../services/api';
import { useHistory } from 'react-router-dom';
import SaveButton from '../../components/SaveButton';
import { PERFIS_RSVP } from '../../util/malhaAereo';
import '../../css/financeiro-parcela-pagar.css';

// Participantes que escolheram a opção — vem do back (`escolhas`). `participantes` conta uma
// vez quem pegou a mesma opção na ida e no retorno; `ida` e `retorno` são por lado. Opção nova,
// ainda sem id, não tem escolha.
const escolhasDe = (option) => ({
    ida: Number(option?.escolhas?.ida ?? 0),
    retorno: Number(option?.escolhas?.retorno ?? 0),
    participantes: Number(option?.escolhas?.participantes ?? 0),
});

// Escolhida por algum participante, de ida ou de retorno: não muda voo nem sai. O back
// recusa de todo jeito; aqui é para ninguém digitar o que não vai ser gravado.
const opcaoTravada = (option) => escolhasDe(option).participantes > 0;

const MOTIVO_TRAVA = 'Escolhida por participante: não se altera nem se remove. Para liberar, resete a etapa de aéreo de quem escolheu.';

// Origem da opção, fixa: importada de uma malha aprovada (`malha_opcao`), manual — digitada aqui
// ou importada por planilha, sem cotação por trás —, ou alterada: veio da malha e teve o voo
// mudado aqui (`atualizado_por`, espelho de `EventoAereo::alterada`), mesmo que volte a ficar
// igual. Opção nova, ainda sem gravar, é manual.
const ehDaMalha = (option) => String(option?.malha_opcao ?? '').trim() !== '';
const ehAlterada = (option) => ehDaMalha(option) && String(option?.atualizado_por ?? '').trim() !== '';


const ORIGIN_EMPTY_KEY = '__sem_origem__';
let uiSequence = 0;

function generateUiToken(prefix) {
    uiSequence += 1;
    return `${prefix}_${uiSequence}`;
}

const IDA_FIELDS = [
    { key: 'idadata', label: 'Data Ida' },
    { key: 'idacompanhia', label: 'Companhia Ida' },
    { key: 'idavoo', label: 'Voo Ida' },
    { key: 'idaorigem', label: 'Origem Ida' },
    { key: 'idadestino', label: 'Destino Ida' },
    { key: 'idahorasaida', label: 'Hora Saída Ida' },
    { key: 'idahorachegada', label: 'Hora Chegada Ida' },
];

const RETORNO_FIELDS = [
    { key: 'retornodata', label: 'Data Retorno' },
    { key: 'retornocompanhia', label: 'Companhia Retorno' },
    { key: 'retornovoo', label: 'Voo Retorno' },
    { key: 'retornoorigem', label: 'Origem Retorno' },
    { key: 'retornodestino', label: 'Destino Retorno' },
    { key: 'retornohorasaida', label: 'Hora Saída Retorno' },
    { key: 'retornohorachegada', label: 'Hora Chegada Retorno' },
];

function createEmptyOption(origin = '', originGroupId = generateUiToken('origin')) {
    return {
        __uiId: generateUiToken('option'),
        __originGroupId: originGroupId,
        aeroportoOrigem: origin,
        idadata: '',
        idacompanhia: '',
        idavoo: '',
        idaorigem: '',
        idadestino: '',
        idahorasaida: '',
        idahorachegada: '',
        retornodata: '',
        retornocompanhia: '',
        retornovoo: '',
        retornoorigem: '',
        retornodestino: '',
        retornohorasaida: '',
        retornohorachegada: '',
    };
}

function getOriginKey(origin) {
    const originLabel = (origin ?? '').trim();
    return originLabel !== '' ? originLabel : ORIGIN_EMPTY_KEY;
}

function getOriginLabel(origin) {
    const originLabel = (origin ?? '').trim();
    return originLabel !== '' ? originLabel : 'Sem origem definida';
}

function optionHasAnyValue(option) {
    return Object.entries(option ?? {}).some(([key, value]) => (
        key !== 'id'
        && !key.startsWith('__')
        && (value ?? '').toString().trim() !== ''
    ));
}

function normalizeOptions(options) {
    const originGroupIds = {};

    return (options ?? []).map((option) => {
        const originValue = option?.aeroportoOrigem ?? '';
        const fallbackOriginKey = getOriginKey(originValue);

        if (!originGroupIds[fallbackOriginKey]) {
            originGroupIds[fallbackOriginKey] = option?.__originGroupId ?? generateUiToken('origin');
        }

        return {
            ...createEmptyOption(originValue, option?.__originGroupId ?? originGroupIds[fallbackOriginKey]),
            ...(option ?? {}),
            __uiId: option?.__uiId ?? generateUiToken('option'),
            __originGroupId: option?.__originGroupId ?? originGroupIds[fallbackOriginKey],
        };
    });
}

function buildOriginGroups(options) {
    const groups = [];
    const groupIndexByKey = {};

    (options ?? []).forEach((option, rowIndex) => {
        const originValue = option?.aeroportoOrigem ?? '';
        const originKey = option?.__originGroupId ?? getOriginKey(originValue);

        if (groupIndexByKey[originKey] === undefined) {
            groupIndexByKey[originKey] = groups.length;
            groups.push({
                key: originKey,
                originValue,
                label: getOriginLabel(originValue),
                items: [],
            });
        }

        groups[groupIndexByKey[originKey]].items.push({
            rowIndex,
            option,
        });
    });

    return groups;
}

function createExpandedState(options) {
    const expanded = {};

    buildOriginGroups(options).forEach((group) => {
        expanded[group.key] = true;
    });

    if (Object.keys(expanded).length === 0) {
        expanded[ORIGIN_EMPTY_KEY] = true;
    }

    return expanded;
}

function renderFieldGrid(fields, option, onChange, disabled = false) {
    return (
        <div className="fin-pp__opcao-campos">
            {fields.map((field) => (
                <FinField label={field.label} key={field.key}>
                    <FinInput
                        type="text"
                        disabled={disabled}
                        value={option?.[field.key] ?? ''}
                        onChange={(event) => onChange(field.key, event.target.value)}
                    />
                </FinField>
            ))}
        </div>
    );
}

export default function CadastroEventoRsvpOpcaoAereo({ match }) {
    const id = match.params.id;
    const empresa = localStorage.getItem('empresa');
    const token = localStorage.getItem('token');
    const [dados, setDados] = useState({ opcoes: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);
    const [expandedOrigins, setExpandedOrigins] = useState({});
    // Dados do evento para o cabeçalho — mesma fonte das outras telas de Aéreo.
    const [headerEvento, setHeaderEvento] = useState({});
    const history = useHistory();
    const params = new URLSearchParams(window.location.search);
    const idParticipante = params.get('idParticipante');
    // A Importação das Opções é do RSVP (mesmo recorte do back): para os outros perfis o atalho nem aparece.
    const perfil = localStorage.getItem('perfil');
    const podeImportar = perfil === 'admin' || PERFIS_RSVP.includes(perfil);
    // A origem da opção (da malha, alterada, manual) é assunto da importação da malha, que não é do
    // operador de RSVP: para ele os badges e os contadores de origem somem, e a opção é só a opção.
    const mostraOrigem = perfil !== 'operadorrsvp';

    const buscar = () => {
        setIsLoading(true);
        if (!id) {
            setDados({ opcoes: [] });
            setExpandedOrigins(createExpandedState([]));
            setIsLoading(false);
            return;
        }

        api({
            method: 'get',
            action: '/eventoAereo',
            params: {
                evento: id,
                idParticipante,
            },
            token,
        }).then((response) => {
            const dadosRecebidos = response.data.data;
            const opcoes = normalizeOptions(dadosRecebidos.opcoes ?? []);

            setDados({
                ...dadosRecebidos,
                opcoes,
            });
            setExpandedOrigins(createExpandedState(opcoes));
        }).catch(() => {
            toast.error('Erro ao carregar opções aéreas');
        }).finally(() => { setIsLoading(false); });
    };

    const updateOptions = (nextOptions) => {
        setDados((current) => ({
            ...current,
            opcoes: nextOptions,
        }));
    };

    const updateOptionField = (rowIndex, field, value) => {
        const nextOptions = [...(dados.opcoes ?? [])];
        nextOptions[rowIndex] = {
            ...createEmptyOption('', nextOptions[rowIndex]?.__originGroupId),
            ...(nextOptions[rowIndex] ?? {}),
            [field]: value,
        };
        updateOptions(nextOptions);
    };

    const updateOriginForGroup = (group, value) => {
        const nextOptions = [...(dados.opcoes ?? [])];

        group.items.forEach(({ rowIndex }) => {
            nextOptions[rowIndex] = {
                ...createEmptyOption('', nextOptions[rowIndex]?.__originGroupId ?? group.key),
                ...(nextOptions[rowIndex] ?? {}),
                aeroportoOrigem: value,
            };
        });

        updateOptions(nextOptions);
        setExpandedOrigins((current) => ({
            ...current,
            [group.key]: true,
        }));
    };

    const removeOption = (rowIndex) => {
        const nextOptions = [...(dados.opcoes ?? [])];
        nextOptions.splice(rowIndex, 1);
        updateOptions(nextOptions);
    };

    const addOptionToOrigin = (originGroupId, originValue) => {
        updateOptions([
            ...(dados.opcoes ?? []),
            createEmptyOption(originValue, originGroupId),
        ]);
        setExpandedOrigins((current) => ({
            ...current,
            [originGroupId]: true,
        }));
    };

    const addNewOrigin = () => {
        updateOptions([
            ...(dados.opcoes ?? []),
            createEmptyOption(''),
        ]);
        setExpandedOrigins((current) => ({
            ...current,
            [ORIGIN_EMPTY_KEY]: true,
        }));
    };

    const toggleOrigin = (originKey) => {
        setExpandedOrigins((current) => ({
            ...current,
            [originKey]: !current[originKey],
        }));
    };

    const salvar = () => {
        
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        const opcoes = (dados.opcoes ?? []).filter(optionHasAnyValue);
        const opcoesSalvar = opcoes.map((option) => {
            const { __uiId, __originGroupId, ...optionSalvar } = option;
            return optionSalvar;
        });

        api({
            method: 'put',
            action: '/eventoAereo/' + id,
            token,
            data: {
                participante: idParticipante,
                evento: id,
                empresa,
                opcoes: opcoesSalvar,
            },
        }).then(() => {
            toast.success('Opções aéreas salvas');

            if (idParticipante) {
                history.push('/cadastroEventoParticipante/' + idParticipante);
                return;
            }

            history.push('/cadastroEventoRsvp/' + id);
        }).catch((error) => {
            toast.error('Erro ao salvar: ' + (error.response?.data?.error ?? error.message ?? ''));
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    useEffect(() => {
        buscar();
        if (id) {
            api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { evento: id } })
                .then(res => setHeaderEvento(res.data?.data ?? {}))
                .catch(() => {});
        }
        // eslint-disable-next-line
    }, []);

    const gruposOrigem = buildOriginGroups(dados.opcoes);
    const normalizarId = (valor) => {
        if (valor === null || valor === undefined) return '';
        if (typeof valor === 'object') return String(valor.id ?? '');
        return String(valor);
    };

    // No cadastro do participante, toda opção listada é vinculada a ele: o back só lista essas.
    const opcaoIdSelecionada = (option, campoEscolha) => (
        normalizarId(dados.participante?.id) !== ''
        && normalizarId(option?.id) !== ''
        && normalizarId(option?.id) === normalizarId(dados.participante?.[campoEscolha])
    );

    // Contagens do cabeçalho. Só as linhas com algum valor: a opção em branco do "Adicionar"
    // não é gravada, então também não conta.
    const preenchidas = (dados.opcoes ?? []).filter(optionHasAnyValue);
    const alteradas = preenchidas.filter(ehAlterada).length;
    const daMalha = preenchidas.filter(ehDaMalha).length - alteradas;
    const manuais = preenchidas.length - daMalha - alteradas;
    const escolhidas = preenchidas.filter(opcaoTravada).length;
    const comPortados = preenchidas.filter(o => Number(o.portados) > 0);
    const vinculosParticipantes = comPortados.reduce((n, o) => n + Number(o.portados), 0);
    const modoParticipante = !!dados.participante?.id;
    const plural = (n, um, varios) => (n === 1 ? um : varios);

    // De onde vêm as opções: da malha, alteradas depois de importadas, ou manuais. Contam igual no
    // evento e no cadastro do participante.
    const indicadoresDeOrigem = [
        { rotulo: 'Da malha', valor: daMalha, tom: 'ok',
            titulo: 'Importadas de malha aprovada: o voo vem da cotação.' },
        { rotulo: plural(alteradas, 'Alterada', 'Alteradas'), valor: alteradas, tom: 'warn',
            titulo: 'Vieram de malha aprovada e tiveram o voo mudado aqui. A origem fica marcada, e elas não voltam a ser da malha.' },
        { rotulo: plural(manuais, 'Manual', 'Manuais'), valor: manuais, tom: 'warn',
            titulo: 'Digitadas aqui ou importadas por planilha, sem vínculo com malha aprovada.' },
    ];

    const indicadores = modoParticipante ? [
        { rotulo: plural(preenchidas.length, 'Opção do participante', 'Opções do participante'), valor: preenchidas.length, tom: 'info',
            titulo: 'Vinculadas a este participante: criadas nesta tela ou importadas na Importação das Opções de Aéreo.' },
        ...(mostraOrigem ? indicadoresDeOrigem : []),
        { rotulo: plural(escolhidas, 'Escolhida', 'Escolhidas'), valor: escolhidas,
            titulo: 'Já escolhidas por algum participante (ida ou retorno). Não se alteram nem se removem.' },
        { rotulo: plural(gruposOrigem.length, 'Origem', 'Origens'), valor: gruposOrigem.length },
    ] : [
        { rotulo: plural(preenchidas.length, 'Opção no evento', 'Opções no evento'), valor: preenchidas.length, tom: 'info',
            titulo: 'Opções do evento todo. As que só os participantes vinculados veem ficam no cadastro de cada um e na Importação das Opções de Aéreo.' },
        ...(mostraOrigem ? indicadoresDeOrigem : []),
        { rotulo: plural(escolhidas, 'Escolhida', 'Escolhidas'), valor: escolhidas,
            titulo: 'Já escolhidas por algum participante (ida ou retorno). Não se alteram nem se removem.' },
        { rotulo: plural(vinculosParticipantes, 'Vínculo c/ participante', 'Vínculos c/ participantes'), valor: vinculosParticipantes,
            titulo: comPortados.length
                ? `${comPortados.length} ${comPortados.length === 1 ? 'opção também vinculada' : 'opções também vinculadas'} a participantes, na Importação das Opções de Aéreo ou no cadastro deles.`
                : 'Nenhuma opção vinculada a participante.' },
        { rotulo: plural(gruposOrigem.length, 'Origem', 'Origens'), valor: gruposOrigem.length },
    ];

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid fin-pp">
                {/* O `id` desta rota já é o do evento: é com ele que a Importação das Opções e o
                    cadastro do participante abrem esta tela, e é ele que vai como `evento` para a API. */}
                <CabecalhoEventoAereo
                    titulo={modoParticipante ? 'Opções de Aéreo do participante' : 'Opções de Aéreo'}
                    nomeEvento={headerEvento.nomeEvento || dados.evento?.nomersvp || dados.evento?.nome}
                    cliente={headerEvento.cliente}
                    solicitante={headerEvento.solicitante}
                    dataEvento={headerEvento.dataEvento}
                    localEvento={headerEvento.localEvento}
                    // Sem `tktMedio` nem `voosSolicitados`: esta tela é do evento inteiro.
                    atalhos={[
                        podeImportar && {
                            rotulo: 'Importação das Opções',
                            titulo: 'Importa as opções das malhas aprovadas para o participante escolher.',
                            to: '/cadastroEventoPortacaoAereo/' + id,
                        },
                        {
                            rotulo: 'Solicitações',
                            titulo: 'Painel de Solicitações deste evento.',
                            to: '/cadastroEventoSolicitacoesAereo/' + id,
                        },
                    ]}
                    indicadores={indicadores}
                />

                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                    <p className="fin-pp__subtitle" style={{ margin: 0 }}>
                        Cada bloco agrupa as opções de uma cidade de origem. Ajuste um voo específico sem precisar reimportar a planilha.
                    </p>
                    <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary"
                        onClick={addNewOrigin}>
                        <FaPlus size={12} />
                        Adicionar nova origem
                    </button>
                </div>

                {dados.participante?.id ? (
                    <div className="fin-pp__card fin-pp__trecho-head mb-3">
                        <span className="fin-pp__cphead-ic"><FaUser size={14} /></span>
                        <span className="fin-pp__trecho-txt">
                            <span className="fin-pp__cphead-title">{dados.participante?.nome ?? ''}</span>
                            <span className="fin-pp__cphead-sub">Participante</span>
                        </span>
                    </div>
                ) : null}

                <form className="form" onSubmit={(event) => { salvar(); event.preventDefault(); }}>
                    {gruposOrigem.length === 0 ? (
                        <div className="fin-pp__card fin-pp__empty mb-3">
                            Nenhuma opção cadastrada. Adicione uma origem ou importe um arquivo XLS.
                        </div>
                    ) : null}

                    {gruposOrigem.map((group) => {
                        const isExpanded = expandedOrigins[group.key] ?? true;
                        // A cidade vale para o bloco inteiro: com uma escolhida dentro, não troca. No
                        // participante, com uma do evento todo dentro também não (é editada no evento).
                        const origemEscolhida = group.items.some(({ option }) => opcaoTravada(option));
                        const origemDoEvento = modoParticipante && group.items.some(({ option }) => option.geral);
                        const origemTravada = origemEscolhida || origemDoEvento;

                        return (
                            <div className="fin-pp__card mb-3" key={group.key} style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__trecho-head">
                                    <button type="button" className="fin-pp__trecho-toggle"
                                        onClick={() => toggleOrigin(group.key)}>
                                        <span className="fin-pp__cphead-ic"><FaMapMarkerAlt size={14} /></span>
                                        <span className="fin-pp__trecho-txt">
                                            <span className="fin-pp__cphead-title">{group.label}</span>
                                            <span className="fin-pp__cphead-sub">
                                                {group.items.length} {group.items.length > 1 ? 'opções cadastradas' : 'opção cadastrada'}
                                            </span>
                                        </span>
                                    </button>

                                    <div className="d-flex gap-2 flex-shrink-0">
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                            onClick={() => addOptionToOrigin(group.key, group.originValue ?? '')}>
                                            <FaPlus size={11} />
                                            Adicionar opção
                                        </button>
                                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                            aria-label={isExpanded ? 'Recolher origem' : 'Abrir origem'}
                                            onClick={() => toggleOrigin(group.key)}>
                                            {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                        </button>
                                    </div>
                                </div>

                                {isExpanded ? (
                                    <>
                                        <div className="fin-pp__trecho-body">
                                            <div className="fin-pp__filters">
                                                <FinField label="Cidade de origem" style={{ flex: '0 1 420px' }}>
                                                    <FinInput
                                                        type="text"
                                                        disabled={origemTravada}
                                                        title={origemEscolhida
                                                            ? 'Há opção escolhida por participante neste bloco: a cidade não troca. Para liberar, resete a etapa de aéreo de quem escolheu.'
                                                            : origemDoEvento ? 'Há opção do evento todo neste bloco: a cidade se edita nas opções do evento.' : undefined}
                                                        value={group.originValue ?? ''}
                                                        onChange={(event) => updateOriginForGroup(group, event.target.value)}
                                                    />
                                                </FinField>
                                            </div>
                                        </div>

                                        {group.items.map(({ rowIndex, option }, optionIndex) => {
                                            const idaSelecionadaPeloParticipante = opcaoIdSelecionada(option, 'escolhaaereoida');
                                            const retornoSelecionadoPeloParticipante = opcaoIdSelecionada(option, 'escolhaaereoretorno');
                                            // A trava vale pela opção inteira, por qualquer participante — não só
                                            // pelo lado escolhido pelo participante desta tela.
                                            const travada = opcaoTravada(option);
                                            // No participante, a do evento todo não muda voo: só sai o vínculo dele.
                                            const doEventoTodo = modoParticipante && !!option.geral;
                                            const escolhas = escolhasDe(option);
                                            const totalEscolhas = escolhas.participantes;

                                            return (
                                                <div className="fin-pp__trecho-item" key={option.__uiId ?? rowIndex}>
                                                    <div className="fin-pp__trecho-head">
                                                        <span className="fin-pp__cphead-ic"><FaPlane size={13} /></span>
                                                        <span className="fin-pp__trecho-txt" style={{ flex: 1 }}>
                                                            <span className="fin-pp__cphead-title d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: 15 }}>
                                                                Opção {optionIndex + 1}
                                                                {/* De onde veio, fixo: manual chama atenção porque não tem cotação
                                                                    aprovada por trás; a da malha é o caminho normal; a alterada
                                                                    veio da malha e mudou aqui. */}
                                                                {!mostraOrigem ? null : ehAlterada(option) ? (
                                                                    <span className="fin-pp__pill is-warn"
                                                                        title="Veio de malha aprovada e teve o voo mudado aqui. A origem fica marcada, mesmo que o voo volte a ficar igual, e a opção não volta a ser da malha.">
                                                                        Alterada
                                                                    </span>
                                                                ) : ehDaMalha(option) ? (
                                                                    <span className="fin-pp__pill is-ok"
                                                                        title="Importada de malha aprovada. Mudar o voo aqui a marca como alterada: para seguir a malha, peça a alteração da malha e importe de novo.">
                                                                        Da malha
                                                                    </span>
                                                                ) : (
                                                                    <span className="fin-pp__pill is-warn"
                                                                        title="Digitada aqui ou importada por planilha, sem vínculo com malha aprovada.">
                                                                        Manual
                                                                    </span>
                                                                )}
                                                                {/* Contagem de escolhas: o motivo do cadeado, e o que o RSVP
                                                                    precisa saber antes de mexer. */}
                                                                {totalEscolhas > 0 && (
                                                                    <span className="fin-pp__pill is-warn"
                                                                        title={`Ida: ${escolhas.ida} · Retorno: ${escolhas.retorno}. ${MOTIVO_TRAVA}`}>
                                                                        <FaLock size={10} />
                                                                        {totalEscolhas} {totalEscolhas === 1 ? 'escolha' : 'escolhas'}
                                                                    </span>
                                                                )}
                                                                {doEventoTodo && (
                                                                    <span className="fin-pp__pill is-info"
                                                                        title="Também está nas opções do evento: o voo se edita lá. Remover aqui tira só o vínculo deste participante.">
                                                                        Do evento todo
                                                                    </span>
                                                                )}
                                                                {/* Vinculada a participantes, na Importação das Opções ou no cadastro deles. No evento,
                                                                    remover tira do evento todo e ela continua só para eles; no participante,
                                                                    tira só o vínculo dele. */}
                                                                {Number(option.portados) > 0 && (
                                                                    <span className="fin-pp__pill is-info"
                                                                        title={modoParticipante
                                                                            ? 'Os vinculados veem esta opção de qualquer cidade. Mudar o voo aqui muda para todos eles; remover tira só o vínculo deste participante.'
                                                                            : 'Os vinculados veem esta opção de qualquer cidade. Removida aqui, ela continua só para eles.'}>
                                                                        Vinculada a {option.portados} {Number(option.portados) === 1 ? 'participante' : 'participantes'}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="fin-pp__cphead-sub">
                                                                Ida e retorno organizados no mesmo conjunto de opção.
                                                            </span>
                                                        </span>

                                                        <span title={travada ? MOTIVO_TRAVA
                                                            : modoParticipante ? 'Tira só o vínculo deste participante. A opção que ficar sem ninguém vinculado, e não for do evento todo, é apagada.' : undefined}>
                                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                                disabled={travada}
                                                                onClick={() => removeOption(rowIndex)}>
                                                                <FaTrash size={11} />
                                                                Remover
                                                            </button>
                                                        </span>
                                                    </div>

                                                    <div className="fin-pp__trecho-body">
                                                        <div className="fin-pp__trecho-sentidos" style={{ marginTop: 0 }}>
                                                            <div className="fin-pp__trecho-sentido">
                                                                <div className="fin-pp__cphead">
                                                                    <span className="fin-pp__cphead-ic"><FaPlaneDeparture size={14} /></span>
                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                            <span className="fin-pp__cphead-title" style={{ fontSize: 15 }}>Trecho de Ida</span>
                                                                            {idaSelecionadaPeloParticipante && (
                                                                                <span className="fin-pp__pill is-ok">Já selecionado pelo participante</span>
                                                                            )}
                                                                        </div>
                                                                        <p className="fin-pp__cphead-sub">Campos da saída do participante até o destino.</p>
                                                                    </div>
                                                                </div>
                                                                {renderFieldGrid(IDA_FIELDS, option, (field, value) => updateOptionField(rowIndex, field, value), travada || doEventoTodo)}
                                                            </div>

                                                            <div className="fin-pp__trecho-sentido">
                                                                <div className="fin-pp__cphead">
                                                                    <span className="fin-pp__cphead-ic"><FaPlaneArrival size={14} /></span>
                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                            <span className="fin-pp__cphead-title" style={{ fontSize: 15 }}>Trecho de Retorno</span>
                                                                            {retornoSelecionadoPeloParticipante && (
                                                                                <span className="fin-pp__pill is-ok">Já selecionado pelo participante</span>
                                                                            )}
                                                                        </div>
                                                                        <p className="fin-pp__cphead-sub">Campos da volta do participante para a origem.</p>
                                                                    </div>
                                                                </div>
                                                                {renderFieldGrid(RETORNO_FIELDS, option, (field, value) => updateOptionField(rowIndex, field, value), travada || doEventoTodo)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : null}
                            </div>
                        );
                    })}

                    <div className="row fixoRodape">
                        <div className="col-12 d-flex justify-content-end">
                            <SaveButton
                                isSubmitting={isSubmitting}
                                className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--primary"
                                iconSize={14}
                            >
                                Salvar alterações
                            </SaveButton>
                        </div>
                    </div>
                </form>
            </div>
        </ErpTemplate>
    );
}
